// src/components/Layout.js - Fixed version
import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import {
  HomeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  UsersIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ShieldCheckIcon,
  CubeTransparentIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline'

export default function Layout({ children }) {
  const { user, signOut, isSuperAdmin } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()

  // Calculate this once to avoid multiple function calls during render
  const isSuper = isSuperAdmin()

  const handleSignOut = async () => {
    try {
      await signOut()
      navigate('/login')
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }

  const navigation = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: HomeIcon,
      available: true
    },
    {
      name: 'Call Logs',
      href: '/calls',
      icon: PhoneIcon,
      available: true
    },
    {
      name: 'Revenue Analytics',
      href: '/revenue',
      icon: CurrencyDollarIcon,
      available: true
    },
    {
      name: 'Customers',
      href: '/customers',
      icon: CubeTransparentIcon,
      available: true
    },
    // Super Admin Only
    {
      name: 'All Businesses',
      href: '/businesses',
      icon: BuildingOfficeIcon,
      available: isSuper,
      adminOnly: true
    },
    {
      name: 'User Management',
      href: '/admin/users',
      icon: UsersIcon,
      available: isSuper,
      adminOnly: true
    },
    // Regular Business Users
    {
      name: 'My Business',
      href: '/business',
      icon: BuildingOfficeIcon,
      available: !isSuper
    },
   
    {
      name: 'Settings',
      href: '/settings',
      icon: Cog6ToothIcon,
      available: true
    }
  ].filter(item => item.available)

  const isActiveRoute = (href) => {
    return location.pathname === href
  }

  const Sidebar = ({ mobile = false }) => (
    <div className="flex flex-col h-full bg-gray-800">
      {/* Logo/Header */}
      <div className="flex items-center justify-between h-16 px-6 bg-gray-900">
        <div className="flex items-center">
          <PhoneIcon className="h-8 w-8 text-white" />
          <span className="ml-2 text-white font-bold text-lg">receptioneer</span>
        </div>
        {mobile && (
          <button
            onClick={() => setSidebarOpen(false)}
            className="text-white hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        )}
      </div>

      {/* User Info */}
      <div className="px-6 py-4 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center">
              <span className="text-sm font-medium text-white">
                {user?.email?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
          </div>
          <div className="ml-3 flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">
              {user?.email || 'User'}
            </p>
            <div className="flex items-center">
              {isSuper && (
                <ShieldCheckIcon className="h-4 w-4 text-yellow-400 mr-1" />
              )}
              <p className="text-xs text-gray-300">
                {isSuper ? 'Super Admin' : 'Business User'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-4 space-y-1 bg-gray-800 overflow-y-auto">
        {navigation.map((item) => (
          <Link
            key={item.name}
            to={item.href}
            onClick={() => mobile && setSidebarOpen(false)}
            className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
              isActiveRoute(item.href)
                ? 'bg-gray-900 text-white'
                : 'text-gray-300 hover:bg-gray-700 hover:text-white'
            }`}
          >
            <item.icon
              className={`mr-3 h-5 w-5 transition-colors ${
                isActiveRoute(item.href) ? 'text-white' : 'text-gray-400 group-hover:text-white'
              }`}
            />
            <span className="flex-1">{item.name}</span>
            {item.adminOnly && (
              <ShieldCheckIcon className="h-4 w-4 text-yellow-400" />
            )}
          </Link>
        ))}
      </nav>

      {/* Sign Out */}
      <div className="px-4 py-4 bg-gray-800 border-t border-gray-700">
        <button
          onClick={handleSignOut}
          className="group flex items-center w-full px-2 py-2 text-sm font-medium text-gray-300 rounded-md hover:bg-gray-700 hover:text-white transition-colors"
        >
          <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-white" />
          Sign Out
        </button>
      </div>
    </div>
  )

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 flex md:hidden">
          <div 
            className="fixed inset-0 bg-gray-600 bg-opacity-75" 
            onClick={() => setSidebarOpen(false)} 
          />
          <div className="relative flex-1 flex flex-col max-w-xs w-full">
            <Sidebar mobile={true} />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <Sidebar />
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden bg-gray-50">
        {/* Mobile header */}
        <div className="relative z-10 flex-shrink-0 flex h-16 bg-white shadow md:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <div className="flex-1 px-4 flex justify-between items-center">
            <h1 className="text-lg font-medium text-gray-900">
              {navigation.find(item => isActiveRoute(item.href))?.name || 'Dashboard'}
            </h1>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          {children}
        </main>
      </div>
    </div>
  )
}